import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import {
  DashboardOutlined,
  LocalShippingOutlined,
  MopedOutlined,
  StorefrontOutlined,
  ReceiptLongOutlined,
  AccountBalanceWalletOutlined,
  AssessmentOutlined,
  BusinessOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../assets/images/logo.png';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Layout';
import { useLanguage } from '../context/LanguageContext';

const Sidebar = ({ mobileOpen, handleDrawerToggle, isCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isRtl, dir, t } = useLanguage();
  const [companyInfo, setCompanyInfo] = useState({ name: 'FastLine', logo: null });

  const navCategories = [
    {
      categoryKey: 'nav.operationsCategory',
      items: [
        { textKey: 'nav.liveOperations', icon: DashboardOutlined, path: '/home', badge: t('nav.liveBadge') },
        { textKey: 'nav.ordersManagement', icon: LocalShippingOutlined, path: '/orders' },
      ],
    },
    {
      categoryKey: 'nav.fleetCategory',
      items: [
        { textKey: 'nav.couriers', icon: MopedOutlined, path: '/couriers' },
      ],
    },
    {
      categoryKey: 'nav.partnersCategory',
      items: [
        { textKey: 'nav.restaurants', icon: StorefrontOutlined, path: '/restaurants' },
        { textKey: 'nav.pricingPlans', icon: ReceiptLongOutlined, path: '/pricing-plans' },
      ],
    },
    {
      categoryKey: 'nav.financeCategory',
      items: [
        { textKey: 'nav.finance', icon: AccountBalanceWalletOutlined, path: '/finance' },
        { textKey: 'nav.reports', icon: AssessmentOutlined, path: '/reports' },
      ],
    },
    {
      categoryKey: 'nav.settingsCategory',
      items: [
        { textKey: 'nav.companyProfile', icon: BusinessOutlined, path: '/company' },
      ],
    },
  ];

  useEffect(() => {
    try {
      const savedUser = localStorage.getItem('fastline_user');
      const savedCompany = localStorage.getItem('fastline_company');
      if (savedCompany) {
        const comp = JSON.parse(savedCompany);
        setCompanyInfo({
          name: comp.companyNameAr || comp.companyNameEn || 'FastLine',
          logo: comp.logoUrl,
        });
      } else if (savedUser) {
        const u = JSON.parse(savedUser);
        setCompanyInfo({
          name: u.companyName || 'FastLine',
          logo: null,
        });
      }
    } catch (e) {}
  }, []);

  const handleNav = (path) => {
    navigate(path);
    if (mobileOpen) {
      handleDrawerToggle();
    }
  };

  const drawerAnchor = 'left';

  const sidebarContent = (
    <Box
      dir={dir}
      style={{ direction: dir }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        color: 'text.primary',
        userSelect: 'none',
        direction: dir,
      }}
    >
      {/* Header with Dynamic Company Logo / Name */}
      <Box
        sx={{
          height: 64,
          px: isCollapsed ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          onClick={() => navigate('/home')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            width: '100%',
            justifyContent: 'center',
            py: 0.5,
          }}
        >
          {companyInfo.logo ? (
            <Box
              component="img"
              src={companyInfo.logo}
              alt={companyInfo.name || 'FastLine'}
              sx={{
                height: isCollapsed ? 30 : 42,
                maxWidth: isCollapsed ? 36 : '100%',
                maxHeight: 46,
                objectFit: 'contain',
                transition: 'all 0.2s ease',
              }}
            />
          ) : (
            <Box
              component="img"
              src={Logo}
              alt="FastLine"
              sx={{
                height: isCollapsed ? 30 : 40,
                maxWidth: isCollapsed ? 36 : '100%',
                maxHeight: 44,
                objectFit: 'contain',
                transition: 'all 0.2s ease',
              }}
            />
          )}
        </Box>

        {mobileOpen && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseOutlined fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Navigation List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1, py: 1.2 }}>
        {navCategories.map((section, idx) => (
          <Box key={idx} sx={{ mb: 1.5 }}>
            {!isCollapsed && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.2,
                  py: 0.6,
                  mb: 0.4,
                  borderTop: idx > 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  pt: idx > 0 ? 1 : 0.4,
                  textAlign: isRtl ? 'right' : 'left',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    letterSpacing: 0.2,
                    textTransform: 'uppercase',
                    textAlign: isRtl ? 'right' : 'left',
                    width: '100%',
                  }}
                >
                  {t(section.categoryKey)}
                </Typography>
              </Box>
            )}

            <List disablePadding>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isSelected = location.pathname === item.path;

                return (
                  <ListItemButton
                    key={item.path}
                    onClick={() => handleNav(item.path)}
                    sx={{
                      my: 0.3,
                      borderRadius: '8px',
                      px: isCollapsed ? 1 : 1.5,
                      py: 0.8,
                      minHeight: 38,
                      bgcolor: isSelected ? 'primary.main' : 'transparent',
                      color: isSelected ? '#FFFFFF' : 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'row',
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      gap: 1.2,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 'auto',
                        color: isSelected ? '#FFFFFF' : 'text.secondary',
                        justifyContent: 'center',
                        m: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 19 }} />
                    </ListItemIcon>

                    {!isCollapsed && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexGrow: 1,
                          textAlign: isRtl ? 'right' : 'left',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.82rem',
                            fontWeight: isSelected ? 700 : 500,
                            color: isSelected ? '#FFFFFF' : 'inherit',
                            textAlign: isRtl ? 'right' : 'left',
                          }}
                        >
                          {t(item.textKey)}
                        </Typography>

                        {item.badge && (
                          <Chip
                            label={item.badge}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              bgcolor: isSelected ? 'rgba(255, 255, 255, 0.25)' : 'error.main',
                              color: '#FFFFFF',
                              px: 0.3,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Clean Footer */}
      <Box
        sx={{
          p: 1.2,
          borderTop: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600 }}>
          {t('appName')} v2.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      dir={dir}
      style={{ direction: dir }}
      sx={{
        width: { lg: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH },
        flexShrink: { lg: 0 },
        transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor={drawerAnchor}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIDEBAR_WIDTH,
            borderRight: '1px solid',
            borderLeft: 'none',
            borderColor: 'divider',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Desktop Permanent Drawer */}
      <Drawer
        variant="permanent"
        anchor={drawerAnchor}
        sx={{
          display: { xs: 'none', lg: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
            borderRight: '1px solid',
            borderLeft: 'none',
            borderColor: 'divider',
            transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {sidebarContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
